import { useState } from "react";
import { useContainerWidth } from "./useContainerWidth";

interface StackedBarChartProps {
  /** Each row: { d: "2026-05-15", purpose1: 5, purpose2: 3, ... } */
  data: Array<Record<string, number | string>>;
  /** Stack series keys in render order (bottom to top). */
  keys: string[];
  /** Optional palette indexed by `keys`. Defaults to a categorical scheme. */
  colors?: string[];
  /** Labels for the legend, keyed by series name. */
  labels?: Record<string, string>;
  height?: number;
}

const DEFAULT_PALETTE = [
  "var(--color-accent)",
  "#4F8DFF",
  "#A37CFF",
  "#F9B23A",
  "#F26B6B",
  "#2DC8A6",
  "#D88AA0",
  "#6E7681",
  "#FF8552",
  "#7BC47F",
];

/**
 * Stacked bar chart for daily breakdowns. Renders as SVG so we don't need a
 * charting lib. The SVG viewBox tracks the real container width (via
 * ResizeObserver), so `preserveAspectRatio` stays uniform — text doesn't
 * get squashed when the parent column is wider/narrower than the canvas.
 */
export function StackedBarChart({
  data,
  keys,
  colors = DEFAULT_PALETTE,
  labels = {},
  height = 240,
}: StackedBarChartProps) {
  const [hostRef, hostWidth] = useContainerWidth();
  /**
   * Index of the bar currently under the pointer. `null` when nothing is
   * hovered, which keeps the tooltip out of the DOM and the legend's
   * focus styles uncluttered.
   */
  const [hovered, setHovered] = useState<number | null>(null);

  if (data.length === 0 || keys.length === 0) {
    return (
      <div
        className="grid place-items-center rounded-2xl border border-border bg-surface text-sm text-text-tertiary"
        style={{ height }}
      >
        Sem dados no período.
      </div>
    );
  }

  // Fall back to 720 on the very first render before the ResizeObserver
  // reports a width. Sub-second visual artifact; the next paint gets the
  // real container width.
  const width = hostWidth > 0 ? hostWidth : 720;
  const padX = 40;
  const padTop = 16;
  const padBottom = 32;
  const chartW = width - padX * 2;
  const chartH = height - padTop - padBottom;

  // Total per day → find max for Y scaling.
  const totals = data.map((row) =>
    keys.reduce((acc, k) => acc + (Number(row[k]) || 0), 0)
  );
  const max = Math.max(...totals, 1);

  const barGap = 2;
  const barWidth = Math.max(2, chartW / data.length - barGap);

  // Pick tick labels — at most 5, always include first + last.
  const tickCount = Math.min(5, data.length);
  const tickIndices: number[] = [];
  for (let i = 0; i < tickCount; i++) {
    tickIndices.push(
      Math.round(((data.length - 1) * i) / Math.max(tickCount - 1, 1))
    );
  }

  function shortDate(iso: string): string {
    const dt = new Date(iso + "T00:00:00Z");
    return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  }

  function fullDate(iso: string): string {
    const dt = new Date(iso + "T00:00:00Z");
    return dt.toLocaleDateString("pt-BR", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  // Tooltip position: anchored to the hovered bar's center, just above the
  // top of the chart so it never overlaps the bar itself. We compute in SVG
  // user-space units then translate to CSS percentages on the wrapper.
  const tooltipLeftPct =
    hovered !== null
      ? ((padX + hovered * (chartW / data.length) + barWidth / 2) / width) * 100
      : 0;
  const tooltipRow = hovered !== null ? data[hovered] : null;

  return (
    <div
      ref={hostRef}
      className="relative rounded-2xl border border-border bg-surface p-5"
      onMouseLeave={() => setHovered(null)}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block max-w-full"
        role="img"
      >
        {/* Baseline + max guide */}
        <line
          x1={padX}
          y1={padTop + chartH}
          x2={padX + chartW}
          y2={padTop + chartH}
          stroke="var(--color-border)"
          strokeWidth={1}
        />
        <line
          x1={padX}
          y1={padTop}
          x2={padX + chartW}
          y2={padTop}
          stroke="var(--color-border)"
          strokeDasharray="3 3"
          strokeWidth={1}
        />
        {/* Highlight band behind the hovered day. Drawn first so the bar
            segments paint on top. */}
        {hovered !== null && (
          <rect
            x={padX + hovered * (chartW / data.length)}
            y={padTop}
            width={chartW / data.length}
            height={chartH}
            fill="var(--color-text)"
            fillOpacity={0.04}
            rx={2}
            pointerEvents="none"
          />
        )}
        {data.map((row, i) => {
          const xStart = padX + i * (chartW / data.length) + barGap / 2;
          let yCursor = padTop + chartH;
          return (
            <g key={String(row.d)}>
              {keys.map((k, kIdx) => {
                const value = Number(row[k]) || 0;
                if (value === 0) return null;
                const segH = (value / max) * chartH;
                const y = yCursor - segH;
                yCursor = y;
                return (
                  <rect
                    key={k}
                    x={xStart}
                    y={y}
                    width={barWidth}
                    height={segH}
                    fill={colors[kIdx % colors.length]}
                    rx={1}
                    pointerEvents="none"
                  />
                );
              })}
              {/* Invisible hit area covering the full bar slot, including
                  the empty space above (so the user doesn't have to land on
                  the colored pixels). Width matches the per-day slot, not
                  just barWidth, so adjacent slots butt against each other. */}
              <rect
                x={padX + i * (chartW / data.length)}
                y={padTop}
                width={chartW / data.length}
                height={chartH}
                fill="transparent"
                onMouseEnter={() => setHovered(i)}
              />
            </g>
          );
        })}
        {/* Y labels */}
        <text
          x={padX - 8}
          y={padTop + 4}
          textAnchor="end"
          fontSize={11}
          fill="var(--color-text-tertiary)"
        >
          {max}
        </text>
        <text
          x={padX - 8}
          y={padTop + chartH + 4}
          textAnchor="end"
          fontSize={11}
          fill="var(--color-text-tertiary)"
        >
          0
        </text>
        {/* X labels */}
        {tickIndices.map((idx, n) => {
          const row = data[idx];
          if (!row) return null;
          const x = padX + idx * (chartW / data.length) + barWidth / 2;
          return (
            <text
              key={n}
              x={x}
              y={height - 10}
              textAnchor={
                idx === 0
                  ? "start"
                  : idx === data.length - 1
                    ? "end"
                    : "middle"
              }
              fontSize={11}
              fill="var(--color-text-tertiary)"
            >
              {shortDate(String(row.d))}
            </text>
          );
        })}
      </svg>

      {tooltipRow && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-2 rounded-lg border border-border bg-surface px-3 py-2 text-xs shadow-[var(--shadow-card-lift)]"
          // Bar slot center → percentage of the container width. The 5
          // padding the wrapper uses (`p-5`, 20px) is accounted for by
          // the wrapper's box-sizing; offsetting within the inner svg
          // viewport gives the right anchor for any width.
          style={{
            left: `calc(${tooltipLeftPct}% + 20px)`,
            top: 20,
          }}
        >
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-text-tertiary">
            {fullDate(String(tooltipRow.d))}
          </p>
          <ul className="mt-2 grid gap-1">
            {keys.map((k, i) => {
              const value = Number(tooltipRow[k]) || 0;
              if (value === 0) return null;
              return (
                <li
                  key={k}
                  className="flex items-center justify-between gap-4 text-text"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 shrink-0 rounded-sm"
                      style={{ background: colors[i % colors.length] }}
                      aria-hidden
                    />
                    {labels[k] ?? k}
                  </span>
                  <span className="font-medium">
                    {value.toLocaleString("pt-BR")}
                  </span>
                </li>
              );
            })}
            <li className="mt-1 flex items-center justify-between gap-4 border-t border-border pt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-secondary">
              <span>Total</span>
              <span>
                {keys
                  .reduce((acc, k) => acc + (Number(tooltipRow[k]) || 0), 0)
                  .toLocaleString("pt-BR")}
              </span>
            </li>
          </ul>
        </div>
      )}

      {/* Legend */}
      <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-text-secondary">
        {keys.map((k, i) => (
          <li key={k} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ background: colors[i % colors.length] }}
              aria-hidden
            />
            {labels[k] ?? k}
          </li>
        ))}
      </ul>
    </div>
  );
}
